import { Badge, Box, Divider, Group, Menu, Portal, Stack, TextInput, Tooltip, useMantineTheme } from '@mantine/core';
import { IconDotsVertical, IconDownload, IconStar, IconTrashX } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { DeviceFormData } from 'components/forms/device/DeviceForm';
import { UseFormReturnType } from '@mantine/form';
import { isEmpty, noop } from 'lodash';
import P1Medium from 'components/typography/P1Medium';
import FileLink from 'components/files/FileLink';
import P2Regular from 'components/typography/P2Regular';
import P3Regular from 'components/typography/P3Regular';
import useFileDownload from 'api/use-file-download';
import FileThumbnail from 'components/files/FileThumbnail';
import { useConfirm } from 'components/modals/message/MessageProvider';
import Slider from 'components/modals/slider/Slider';
import {
  DEVICE_FILE_FORMS_TAB,
  DEVICE_FILE_TYPE_NAME_MAP,
  DeviceFileTabValue,
  DeviceFileType,
  isFormFileType,
  isRevisionFileType,
} from 'model/DeviceFileType';

import ActionIcon from 'components/ActionIcon';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
interface DeviceFileTabContentProps {
  form?: UseFormReturnType<DeviceFormData>;
  files: DeviceFormData['files'];
  tabName: DeviceFileTabValue;
  readonly?: boolean;
  canManageFileType?: (fileType: DeviceFileType) => boolean;
}

/**
 * The tab content of the Device files section.
 */
export default function DeviceFileTabContent({
  form,
  files,
  tabName,
  readonly,
  canManageFileType,
}: DeviceFileTabContentProps) {
  const theme = useMantineTheme();
  const { isMobile } = useResponsiveDesign();
  const { download } = useFileDownload();
  const { confirm } = useConfirm();
  const [openedSlider, setOpenSlider] = useState(false);
  const [initialFileId, setInitialFileId] = useState('');
  const [hideSliderControls, setHideSliderControls] = useState(false);

  const isFormsTab = tabName === DEVICE_FILE_FORMS_TAB;
  const isRevisionTab = tabName === 'revision';

  const tabFiles = useMemo(
    () =>
      files
        .map((f, globalIndex) => ({ ...f, globalIndex }))
        .filter((f) => {
          if (isFormsTab) {
            return isFormFileType(f.type);
          }

          if (isRevisionTab) {
            return isRevisionFileType(f.type);
          }

          return f.type === tabName;
        })
        .filter((f) => (canManageFileType ? canManageFileType(f.type) : true)),
    [files, tabName, isFormsTab, isRevisionTab, canManageFileType]
  );

  const fileIds = useMemo(() => tabFiles.filter(({ deleted }) => !deleted).map(({ fileId }) => fileId), [tabFiles]);

  /** Toggles the file as the main image while preserving the at-most-one invariant. */
  const toggleMainImage = (globalIndex: number) => {
    if (files[globalIndex].isMainImage) {
      form!.setFieldValue(`files.${globalIndex}.isMainImage`, false);
      return;
    }

    // ensure that the main image is only one
    form!.setFieldValue(
      'files',
      files.map((file) => ({ ...file, isMainImage: false }))
    );
    form!.setFieldValue(`files.${globalIndex}.isMainImage`, true);
  };

  /**
   * Removes the file from the form
   */
  const deleteFile = (globalIndex: number) => {
    // soft delete the file
    form!.setFieldValue(`files.${globalIndex}.deleted`, true);

    // mark other image as the main one if the main got deleted
    if (tabName === 'photos') {
      const isMainImage = form!.getInputProps(`files.${globalIndex}.isMainImage`).value;
      if (isMainImage) {
        const nextMainImage = files.find(
          ({ deleted, isMainImage, type }) => !deleted && !isMainImage && type === 'photos'
        );
        if (nextMainImage) {
          form!.setFieldValue(`files.${files.indexOf(nextMainImage)}.isMainImage`, true);
        }
      }
    }
  };

  /**
   * Confirms the file deletion.
   */
  const handleDelete = useCallback(
    (globalIndex: number) =>
      confirm({
        title: 'Potvrdenie zmazania',
        content: 'Naozaj si prajete vymazať súbor?',
        onConfirm: () => deleteFile(globalIndex),
      }),
    [deleteFile]
  );

  const gridStyle = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: `${tabName === 'photos' ? '24px ' : ''}80px 4fr${isFormsTab ? ' 2fr' : ''} 3fr 4fr 40px`,
      gap: '24px',
      borderRadius: '4px',
      alignItems: 'center',
    }),
    [tabName, isFormsTab]
  );

  return (
    <Box pt={24}>
      {!isMobile && (
        <Box sx={gridStyle} bg="gray.0" p={16}>
          {tabName === 'photos' && <P1Medium color="gray.8"></P1Medium>}
          <P1Medium color="gray.8" pl={4}>
            Náhľad
          </P1Medium>
          <P1Medium color="gray.8">Názov</P1Medium>
          {isFormsTab && <P1Medium color="gray.8">Typ formulára</P1Medium>}
          <P1Medium color="gray.8">Súbor</P1Medium>
          <P1Medium color="gray.8">Nahratý</P1Medium>
        </Box>
      )}
      {(isEmpty(tabFiles) || tabFiles.every(({ deleted }) => deleted)) && (
        <Box py={24} px={16}>
          <P1Medium> Momentálne nie sú nahraté žiadne súbory</P1Medium>
        </Box>
      )}
      {/* The rows with individual files */}
      {tabFiles.map((file) =>
        file.deleted ? null : (
          <Box key={file.key}>
            {isMobile ? (
              <Stack spacing={16} p={16}>
                {tabName === 'photos' &&
                  (form?.getInputProps(`files.${file.globalIndex}.isMainImage`).value || file.isMainImage ? (
                    <Group
                      style={{ cursor: readonly ? 'not-allowed' : 'pointer' }}
                      onClick={() => (readonly ? noop : toggleMainImage(file.globalIndex))}
                    >
                      <Tooltip
                        label={readonly ? 'Hlavný obrázok' : 'Zrušiť hlavný obrázok'}
                        withArrow={false}
                        openDelay={500}
                      >
                        <IconStar stroke={2} color="#E6B24E" fill="#E6B24E" />
                      </Tooltip>
                      <P2Regular>{readonly ? 'Hlavný obrázok' : 'Zrušiť hlavný obrázok'}</P2Regular>
                    </Group>
                  ) : (
                    <Group
                      style={{ cursor: readonly ? 'not-allowed' : 'pointer' }}
                      onClick={() => (readonly ? noop : toggleMainImage(file.globalIndex))}
                    >
                      <Tooltip label="Nastaviť ako hlavný obrázok" withArrow={false} openDelay={500}>
                        <IconStar stroke={2} color={`${theme.colors.gray[5]}`} />
                      </Tooltip>
                      <P2Regular>Nastaviť ako hlavný obrázok</P2Regular>
                    </Group>
                  ))}
                <Stack spacing={8}>
                  <P3Regular color="gray.6">Náhľad</P3Regular>
                  <Group w={80}>
                    <FileThumbnail
                      fileId={file.fileId}
                      onOpenSlider={() => {
                        setInitialFileId(file.fileId);
                        setOpenSlider(true);
                        setHideSliderControls(tabName !== 'photos');
                      }}
                    />
                  </Group>
                </Stack>
                <Stack spacing={8}>
                  <P3Regular color="gray.6">Názov</P3Regular>
                  <TextInput
                    size="sm"
                    value={file.fileName}
                    onChange={readonly ? noop : form!.getInputProps(`files.${file.globalIndex}.fileName`).onChange}
                    readOnly={readonly}
                    name={`objekto-files.${tabName}.${file.globalIndex}.fileName`}
                    rightSection={
                      file.hasKepSignature && (
                        <Badge color="green" size="sm" variant="filled">
                          KEP
                        </Badge>
                      )
                    }
                    styles={{
                      rightSection: {
                        width: file.hasKepSignature ? 'auto' : undefined,
                        paddingRight: file.hasKepSignature ? 8 : undefined,
                      },
                    }}
                  />
                </Stack>
                {isFormsTab && (
                  <Stack spacing={8}>
                    <P3Regular color="gray.6">Typ formulára</P3Regular>
                    <P2Regular>{DEVICE_FILE_TYPE_NAME_MAP[file.type]}</P2Regular>
                  </Stack>
                )}
                <Stack spacing={8}>
                  <P3Regular color="gray.6">Súbor</P3Regular>
                  <FileLink
                    fileId={file.fileId}
                    onOpenSlider={() => {
                      setInitialFileId(file.fileId);
                      setOpenSlider(true);
                      setHideSliderControls(tabName !== 'photos');
                    }}
                  />
                </Stack>
                <Stack spacing={8}>
                  <P3Regular color="gray.6">Nahratý</P3Regular>
                  <P2Regular>{file.uploadedAt.toLocaleString()}</P2Regular>
                </Stack>
                <Group position="right">
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'} aria-label="Akcie súboru">
                        <IconDotsVertical stroke="1.5" />
                      </ActionIcon>
                    </Menu.Target>
                    <Portal>
                      <Menu.Dropdown>
                        <Menu.Item
                          onClick={() => {
                            download(file.fileId);
                          }}
                          icon={<IconDownload stroke={1.5} size={20} />}
                        >
                          Stiahnuť
                        </Menu.Item>
                        {!readonly && (
                          <Menu.Item
                            color="red.8"
                            onClick={() => handleDelete(file.globalIndex)}
                            icon={<IconTrashX stroke={1.5} size={20} />}
                          >
                            Vymazať
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Portal>
                  </Menu>
                </Group>
              </Stack>
            ) : (
              <Box sx={gridStyle} p={16}>
                {tabName === 'photos' &&
                  (form?.getInputProps(`files.${file.globalIndex}.isMainImage`).value || file.isMainImage ? (
                    <Group
                      w={24}
                      style={{ cursor: readonly ? 'not-allowed' : 'pointer' }}
                      onClick={() => (readonly ? noop : toggleMainImage(file.globalIndex))}
                    >
                      <Tooltip
                        label={readonly ? 'Hlavný obrázok' : 'Zrušiť hlavný obrázok'}
                        withArrow={false}
                        openDelay={500}
                      >
                        <IconStar stroke={2} color="#E6B24E" fill="#E6B24E" />
                      </Tooltip>
                    </Group>
                  ) : (
                    <Group
                      w={24}
                      style={{ cursor: readonly ? 'not-allowed' : 'pointer' }}
                      onClick={() => (readonly ? noop : toggleMainImage(file.globalIndex))}
                    >
                      <Tooltip label="Nastaviť ako hlavný obrázok" withArrow={false} openDelay={500}>
                        <IconStar stroke={2} color={`${theme.colors.gray[5]}`} />
                      </Tooltip>
                    </Group>
                  ))}
                <Group w={80}>
                  <FileThumbnail
                    fileId={file.fileId}
                    onOpenSlider={() => {
                      setInitialFileId(file.fileId);
                      setOpenSlider(true);
                      setHideSliderControls(tabName !== 'photos');
                    }}
                  />
                </Group>
                <TextInput
                  size="sm"
                  value={file.fileName}
                  onChange={readonly ? noop : form!.getInputProps(`files.${file.globalIndex}.fileName`).onChange}
                  readOnly={readonly}
                  name={`objekto-files.${tabName}.${file.globalIndex}.fileName`}
                  rightSection={
                    file.hasKepSignature && (
                      <Badge color="green" size="sm" variant="filled">
                        KEP
                      </Badge>
                    )
                  }
                  styles={{
                    rightSection: {
                      width: file.hasKepSignature ? 'auto' : undefined,
                      paddingRight: file.hasKepSignature ? 8 : undefined,
                    },
                  }}
                />
                {isFormsTab && <P2Regular>{DEVICE_FILE_TYPE_NAME_MAP[file.type]}</P2Regular>}
                <FileLink
                  fileId={file.fileId}
                  onOpenSlider={() => {
                    setInitialFileId(file.fileId);
                    setOpenSlider(true);
                    setHideSliderControls(tabName !== 'photos');
                  }}
                />
                <P2Regular>{file.uploadedAt.toLocaleString()}</P2Regular>
                <Menu position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'} aria-label="Akcie súboru">
                      <IconDotsVertical stroke="1.5" />
                    </ActionIcon>
                  </Menu.Target>
                  <Portal>
                    <Menu.Dropdown>
                      <Menu.Item
                        onClick={() => {
                          download(file.fileId);
                        }}
                        icon={<IconDownload stroke={1.5} size={20} />}
                      >
                        Stiahnuť
                      </Menu.Item>
                      {!readonly && (
                        <Menu.Item
                          color="red.8"
                          onClick={() => handleDelete(file.globalIndex)}
                          icon={<IconTrashX stroke={1.5} size={20} />}
                        >
                          Vymazať
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Portal>
                </Menu>
              </Box>
            )}
            <Divider color="gray.1" />
          </Box>
        )
      )}

      <Slider
        key={initialFileId}
        opened={openedSlider}
        fileIds={hideSliderControls ? [initialFileId] : fileIds}
        onClose={() => setOpenSlider(false)}
        initialImageId={initialFileId}
        hideControls={hideSliderControls}
      />
    </Box>
  );
}
